import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import Slide from '@/components/shared/Slide';
import type { RoadmapCompanyConfig, ViewMode } from '@/types/roadmap.types';

interface StatItem {
  value: number;
  suffix?: string;
  label: string;
}

function AnimatedNumber({
  from,
  to,
  suffix = '',
  inView,
}: {
  from: number;
  to: number;
  suffix?: string;
  inView: boolean;
}) {
  const [display, setDisplay] = useState(from);

  useEffect(() => {
    if (!inView) {
      setDisplay(from);
      return;
    }

    const duration = 1200;
    const startTime = performance.now();
    let rafId: number;

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      }
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [inView, from, to]);

  return (
    <>
      <span className="print:hidden">{display.toLocaleString()}{suffix}</span>
      <span className="hidden print:inline">{to.toLocaleString()}{suffix}</span>
    </>
  );
}

interface Props {
  config: RoadmapCompanyConfig;
  mode: ViewMode;
  stats: {
    programCount: number;
    categoryCount: number;
    goCount: number;
    jurisdictionCount: number;
    totalFunding: string;
  };
}

export default function SummarySlide({ config, mode, stats }: Props) {
  const { company, executive_summary } = config;
  const fundingOneliner = executive_summary?.realistic_funding_oneliner;
  const strategicStatement = executive_summary?.strategic_statement;

  const gridRef = useRef<HTMLDivElement>(null);
  const inView = useInView(gridRef, { once: true, margin: '-50px' });

  const statItems: StatItem[] = [
    { value: stats.programCount, label: 'Programs' },
    { value: stats.categoryCount, label: 'Investment Layers' },
    { value: stats.goCount, label: 'Recommended (Go)' },
    { value: stats.jurisdictionCount, label: 'Jurisdictions' },
  ];

  return (
    <Slide mode={mode}>
      <div className="w-full mx-auto text-center flex flex-col items-center justify-center min-h-[60vh] print:min-h-0 print:justify-start">
        
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4 print:mb-2 print:text-[16pt]">
          Executive Summary
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-4 print:mb-3 print:text-[10pt]">
          This roadmap identifies {stats.programCount} eligible programs out of which
          we selected <strong className="text-foreground">{stats.goCount} programs</strong> with
          the highest probability of winning.
        </p>

        {strategicStatement && (
          <p className="text-sm text-muted-foreground/80 leading-relaxed w-3/4 mx-auto text-left mb-6 print:mb-4 print:text-[9pt]">
            {strategicStatement}
          </p>
        )}

        <div
          ref={gridRef}
          className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-border mb-10 print:mb-4 w-full"
        >
          {statItems.map((item) => (
            <div key={item.label} className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 print:py-3">
              <div className="text-4xl sm:text-5xl font-bold text-foreground tracking-tight print:text-[20pt]">
                <AnimatedNumber
                  from={0}
                  to={item.value}
                  suffix={item.suffix}
                  inView={inView}
                />
              </div>
              <p className="mt-2 text-xs font-medium text-muted-foreground uppercase tracking-wider print:text-[8pt]">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        {fundingOneliner && (
          <blockquote className="border-l-4 border-primary/30 pl-4 text-left mx-auto mt-auto max-w-2xl print:pl-3 print:mt-6">
            <p className="text-lg font-semibold leading-relaxed text-muted-foreground italic print:text-[10pt]">
              {fundingOneliner}
            </p>
          </blockquote>
        )}
      </div>
    </Slide>
  );
}
